function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }

        this.handleTabSwitch();
      })
      .catch((e) => {
        console.error(e);
      });
  }

  handleTabSwitch() {
    this.querySelectorAll('.related-products__title .related-products__heading').forEach((item) => {
      item.addEventListener('click', () => {
        const tab = item.dataset.tab;
        this.querySelectorAll('.related-products__title .related-products__heading').forEach((item) =>
          item.classList.remove('active')
        );

        item.classList.add('active');

        // get this web element parent
        if (tab == 'recently') {
          this.parentNode.querySelector('.product-grid').classList.add('d-none');
          this.parentNode.querySelector('.recently-viewed-wrapper').classList.remove('d-none');
        } else {
          this.parentNode.querySelector('.product-grid').classList.remove('d-none');
          this.parentNode.querySelector('.recently-viewed-wrapper').classList.add('d-none');
        }
      });
    });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
    const quickBulkElement = this.closest('quick-order-list') || this.closest('quick-add-bulk');
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class MenuDrawerWrapper extends HTMLElement {
  constructor() {
    super();
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('.menu-drawer__menu--main-link').forEach((element) => {
      element.addEventListener('click', (tabItem) => {
        this.resetTabs();
        element.parentNode.classList.add('active');
      });
    });
  }

  resetTabs() {
    this.querySelectorAll('.menu-drawer__menu--main-link').forEach((element) => {
      element.parentNode.classList.remove('active');
    });
  }
}

customElements.define('menu-drawer-wrapper', MenuDrawerWrapper);

class CardComponent extends HTMLElement {
  constructor() {
    super();

    this.selectedColor = null;
    this.variantsData = null;
    this.properties = null;

    this.mediaContainer = this.querySelector('.card__media');
    // this.swatchLabel = this.querySelector('.card__product--variant');
    this.productSizesBar = this.querySelector('.card__product--sizes');
    this.variantIds = this.querySelector('.product_script');

    var jsonData = JSON.parse(this.variantIds?.textContent);
    this.variantsData = jsonData;

    // hide empty sizes bar
    if (this.productSizesBar?.children.length == 0) {
      this.productSizesBar.style.display = 'none';
    }

    this.removeAdditionalImages();

    this.querySelectorAll('.card__swatches .color_swatch').forEach((element, swatchIndex) => {
      element.addEventListener('click', () => {
        var selectedSwatch = element.dataset.swatch;
        var productUrl = element.dataset.url;
        var productHandle = element.dataset.handle;
        var productStyle = element.dataset.style;
        var productName = element.dataset.name;

        console.log(this,'testing')

        if (productUrl) {
          try {
            element.closest('.card-wrapper').querySelector('a').href = productUrl;
          } catch (error) {
            console.error('Error:', error);
          }
        }

        if (productHandle) {
          try {
            this.querySelectorAll('.card__product--size-bar, .card__badge[data-handle]').forEach((sizeBar) => {
              sizeBar.style.display = sizeBar.dataset.handle == productHandle ? null : 'none';
            });
          } catch (error) {
            console.error('Error:', error);
          }
        }

        if (productHandle) {
          try {
            this.querySelectorAll('.card__information-price[data-handle]').forEach((price) => {
              price.dataset.handle != productHandle ? price.classList.remove('active') : price.classList.add('active');
            }
            );
          } catch (error) {
            console.error('Error:', error);

          }
        }

        this.selectedColor = selectedSwatch;
        if (productStyle && productName) {
          this.properties = {
            '_Style': productStyle,
            '_Name': productName,
          }
        }


        this.createSizeBar();
        this.sizeBarAvailable();
        this.attachEventListener();
        this.removeSwatchActiveLabel();
        element.classList.add('active');

        // this.swatchLabel.textContent = `in ${selectedSwatch.toLowerCase()}`;

        this.mediaContainer.querySelectorAll('.card__img-content').forEach((imgContainerElement) => {
          if (imgContainerElement.dataset.cardImg === selectedSwatch) {
            imgContainerElement.style.display = 'block';
          } else {
            imgContainerElement.style.display = 'none';
          }
        });
      });

      if (element.classList.contains('active')) {
        element.click();
      }
    });
  }

  attachEventListener() {
    this.productSizes = this.querySelectorAll('.card__product--sizes .js-add-size').forEach((item) => {
      // item.removeEventListener('click', () => {});
      item.addEventListener('click', (sizeBtn) => {
        
        if (this.selectedColor) {
          Object.keys(this.variantsData).map((key) => {
            var keys = key.split('_');
            var checkAgainst = item.textContent.replace(/\s/g, '');

            if (keys[1] == checkAgainst && keys[0] == this.selectedColor) {
              this.addSizeToCart(this.variantsData[key],this.properties);
            }
          });
        } else {
          Object.keys(this.variantsData).map((key) => {
            var keys = key.split('_');
            var checkAgainst = item.textContent.replace(/\s/g, '');

            if (keys[1] == checkAgainst) {
              this.addSizeToCart(this.variantsData[key],this.properties);
            }
          });
        }
      });
    });
  }

  createSizeBar() {
    if (this.querySelector('.card__product--sizes')) {
      this.querySelector('.card__product--sizes').innerHTML = '';
      var sizeItemHTML = '';

      Object.keys(this.variantsData).map((variantKey, arrValue) => {
        const variantName = variantKey.split('_')[0];
        const variantSize = variantKey.split('_')[1];

        if (this.selectedColor == variantName) {
          sizeItemHTML += `
            <span 
              class="h-small js-add-size ${!this.variantsData[variantKey][1] ? 'sold-out' : 'available'}" 
              data-variant="${this.variantsData[variantKey][0]} "
            > 
              ${variantSize}
            </span>
          `;
        }
      });

      this.querySelector('.card__product--sizes').innerHTML = sizeItemHTML;
    }
  }

  sizeBarAvailable() {
    this.querySelectorAll('.card__product--sizes .js-add-size').forEach((sizeElement) => {
      const variantID = sizeElement.dataset.variant;

      Object.values(this.variantsData).map((item) => {
        if (variantID == item[0]) {
          if (!item[1]) {
            sizeElement.classList.add('sold-out');
          }
        }
      });
    });
  }

  addSizeToCart(id, properties) {
    let formData = {
      items: [
        {
          id: Array.isArray(id) ? id[0] : id,
          quantity: 1,
          properties: properties,
        },
      ],
    };
    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData),
    })
      .then((response) => {
        if (response.status != 422) {
          this.fetchAndReplaceHtml('CartDrawer-CartItems');
          document.getElementById('CartDrawer-Checkout').removeAttribute('disabled');
          return response.json();
        } else {
          response.json().then((data) => {
            alert(data.message);
          });
        }
      })
      .catch((error) => {
        console.error('Error:', error);
      });
  }

  fetchAndReplaceHtml() {
    fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');

        const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
        for (const selector of selectors) {
          const targetElement = document.querySelector(selector);
          const sourceElement = html.querySelector(selector);

          if (targetElement && sourceElement) {
            document.getElementsByTagName('cart-drawer')[0].open();
            document.getElementsByTagName('cart-drawer')[0].classList.remove('is-empty');
            document.getElementsByTagName('cart-drawer')[0].querySelector('.drawer__inner-empty')?.remove();
            targetElement.replaceWith(sourceElement);
          }
        }
      })
      .catch((e) => {
        console.error(e);
      });

    fetch(`${routes.cart_url}?section_id=cart-icon-bubble`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        document.querySelector('#cart-icon-bubble').innerHTML = html.querySelector('.shopify-section').innerHTML;
      });
  }

  removePriceActiveLabel() {
    this.querySelectorAll('.card__information-price').forEach((element) => element.classList.remove('active'));
  }

  removeSwatchActiveLabel() {
    this.querySelectorAll('.card__swatches .color_swatch').forEach((element) => element.classList.remove('active'));
  }

  removeAdditionalImages() {
    this.querySelectorAll('.card__img-content').forEach((container, containerIndex) => {
      container.querySelectorAll('img').forEach((imgElement, imgIndex) => {
        if (imgIndex > 1) {
          imgElement.remove();
        }
      });
    });
  }
}

customElements.define('card-component', CardComponent);

// Additional JS code to add hover support for Dawn megamenu
function bindMouseOverToMegaMenu(item) {
  item.addEventListener('mouseover', () => {
    item.setAttribute('open', true);
    item.querySelector('.mega-menu__content').addEventListener('mouseover', () => {
      item.setAttribute('open', true);
    });
    item.querySelector('.mega-menu__content').addEventListener('mouseleave', () => {
      item.removeAttribute('open');
    });
    item.addEventListener('mouseleave', () => {
      item.removeAttribute('open');
    });
  });
}
let items = document.querySelector('.header__inline-menu').querySelectorAll('details.mega-menu');
items.forEach(bindMouseOverToMegaMenu);

class ModalDrawer extends HTMLElement {
  constructor() {
    super();
    this.targetModal = null;
    this.isOpen = false;
    this.openModal();
  }

  connectedCallback() {
    var closeDrawer = this.querySelector('.icon-close');

    closeDrawer.addEventListener('click', () => {
      this.closeModal();
    });
  }

  openModal() {
    var openDrawer = document.querySelectorAll('.js-modal-drawer-open');

    openDrawer.forEach((drawerBtn) => {
      drawerBtn.addEventListener('click', () => {
        this.targetModal = drawerBtn.dataset;
        this.classList.add('active');
        this.isOpen = true;
      });
    });
  }

  closeModal() {
    this.classList.remove('active');
    this.isOpen = false;
  }
}

customElements.define('modal-drawer', ModalDrawer);

class GiftCardDrawer extends HTMLElement {
  constructor() {
    super();

    this.targetModal = null;
    this.isOpen = false;

    this.headingPrice = this.querySelector('.gift-card-price');
    this.setCurrentVariant();

    this.openModal();

    // window.navigation.addEventListener('navigate', (event) => {
    //   setTimeout(() => {
    //     this.setCurrentVariant();
    //   }, 500);
    // });
  }

  openModal() {
    var openDrawer = document.querySelectorAll('.js-modal-drawer-open-gift');

    openDrawer.forEach((drawerBtn) => {
      drawerBtn.addEventListener('click', () => {
        setTimeout(() => {
          this.setCurrentVariant();
          this.classList.add('active');
          this.isOpen = true;
        }, 500);
      });
    });
  }

  connectedCallback() {
    var closeDrawer = this.querySelector('.icon-close');

    closeDrawer.addEventListener('click', () => {
      this.closeModal();
    });

    // clicking outside this should closeModal as well
    this.addEventListener('click', (event) => {
      if (event.target === this) {
        this.closeModal();
      }
    });
  }

  setCurrentVariant() {
    var queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const variant = urlParams.get('variant');

    document.querySelector('.product-variant-id').value = variant;
    var checkedPrice = document.querySelector('.product-form__input input:checked').value.replace('.00', '');
    this.headingPrice.innerText = checkedPrice;
  }

  closeModal() {
    this.classList.remove('active');
    this.isOpen = false;
  }
}

customElements.define('gift-drawer', GiftCardDrawer);

class StoreAvailabilityDrawer extends HTMLElement {


  connectedButtons = [];

  constructor() {
    super();

    this.productVariant = null;
    
    this.connectButtons();
    document.addEventListener('custom:reloadStoreAvailability', () => {
      this.connectButtons();
    });

  }

  connectButtons() {

    var openDrawer = document.querySelectorAll('.js-modal-drawer-open-store');

    openDrawer.forEach((drawerBtn) => {

      if (!this.connectedButtons.includes(drawerBtn)) {

        drawerBtn.addEventListener('click', () => {
          this.classList.add('active');
          this.isOpen = true;

          var queryString = window.location.search;
          const urlParams = new URLSearchParams(queryString);

          this.productVariant = urlParams.get('variant');

          this.showStore();
        });
        this.connectedButtons.push(drawerBtn);
      }
    });
  }

  showStore() {
    if (this.productVariant == null) {
      this.productVariant = document.querySelector('.product-form .product-variant-id').getAttribute('value');
    }

    this.querySelectorAll('.store-inventory-container .store-inventory').forEach((variant) => {
      const storeId = variant.dataset.store;
      if (storeId == this.productVariant) {
        variant.querySelectorAll('.store-inventory-item').forEach((store) => {
          store.style.display = 'block';
        });
      } else {
        variant.querySelectorAll('.store-inventory-item').forEach((store) => {
          store.style.display = 'none';
        });
      }
    });
  }

  connectedCallback() {
    var closeDrawer = this.querySelector('.icon-close');

    closeDrawer.addEventListener('click', () => {
      this.closeModal();
    });

    // clicking outside this should closeModal as well
    this.addEventListener('click', (event) => {
      if (event.target === this) {
        this.closeModal();
      }
    });
  }

  closeModal() {
    this.classList.remove('active');
    this.isOpen = false;
  }
}

customElements.define('store-availability-drawer', StoreAvailabilityDrawer);

class ReviewsDrawer extends ModalDrawer {
  connectedButtons = [];

  constructor() {
    super();

    this.connectButtons();
    document.addEventListener('custom:reloadReviewsDrawer', () => {
      this.connectButtons();
    });
  }

  connectButtons() {
    var openDrawer = document.querySelectorAll('.js-modal-drawer-open[data-target="reviews"]');

    openDrawer.forEach((drawerBtn) => {
      if (!this.connectedButtons.includes(drawerBtn)) {
        drawerBtn.addEventListener('click', () => {
          this.classList.add('active');
          this.isOpen = true;
          this.initReviews();
        });
        this.connectedButtons.push(drawerBtn);
      }
    });
  }

  connectedCallback() {
    var closeDrawer = this.querySelector('.icon-close');

    closeDrawer.addEventListener('click', () => {
      this.closeModal();
    });

    // clicking outside this should closeModal as well
    this.addEventListener('click', (event) => {
      if (event.target === this) {
        this.closeModal();
      }
    });
  }

  closeModal() {
    this.classList.remove('active');
    this.isOpen = false;
  }

  initReviews() {
    // Add any initialization logic for reviews here
    // This method will be called when the drawer is opened
  }
}

customElements.define('reviews-drawer', ReviewsDrawer);

class ElementAccordion extends HTMLElement {
  constructor() {
    super();
    this.toggleVisible();
  }

  toggleVisible() {
    this.querySelectorAll('.js-toggle-accordion').forEach((item) => {
      item.addEventListener('click', () => {
        item.parentNode.querySelector('.element-accordion__content').classList.toggle('d-none');
        item.classList.toggle('active');
      });
    });
  }
}

customElements.define('element-accordion', ElementAccordion);

//size guide related
let sizeArr = ['fr', 'us', 'it', 'uk', 'eu'];
function convert(to) {
  let element = document.getElementById('measurement-unit');
  if (element && element.checked) switchToInch();
  else switchToCm();
}
function switchToCm() {
  document.querySelectorAll('.size-in').forEach((element) => {
    element.style.display = 'none';
  });
  document.querySelectorAll('.size-cm').forEach((element) => {
    element.style.display = 'table-cell';
  });
}
function switchToInch() {
  document.querySelectorAll('.size-in').forEach((element) => {
    element.style.display = 'table-cell';
  });
  document.querySelectorAll('.size-cm').forEach((element) => {
    element.style.display = 'none';
  });
}
function changeSize() {
  let dropdown = document.getElementById('size-selector');
  document.querySelectorAll('.size-' + dropdown.value).forEach((element) => {
    element.style.display = 'table-cell';
  });
  sizeArr.forEach((el1) => {
    if (el1 != dropdown.value) {
      document.querySelectorAll('.size-' + el1).forEach((el2) => {
        el2.style.display = 'none';
      });
    }
  });
}
window.addEventListener('load', function (event) {
  // changeSize();
  // convert();
});

class SizeGuideDrawer extends HTMLElement {
  connectedButtons = [];

  constructor() {
    super();

    this.connectButtons();
    document.addEventListener('custom:reloadSizeGuide', () => {
      this.connectButtons();
    });
  }

  connectButtons() {
    document.querySelectorAll('.js-modal-drawer-open-size-guide').forEach((button) => {
      if (!this.connectedButtons.includes(button)) {
        button.addEventListener('click', () => {
          this.classList.add('active');
          this.isOpen = true;
          changeSize();
          convert();
          this.initSizeGuide();
        });
        this.connectedButtons.push(button);
      }
    });
  }

  connectedCallback() {
    var closeDrawer = this.querySelector('.icon-close');

    closeDrawer.addEventListener('click', () => {
      this.closeModal();
    });

    // clicking outside this should closeModal as well
    this.addEventListener('click', (event) => {
      if (event.target === this) {
        this.closeModal();
      }
    });
  }

  closeModal() {
    this.classList.remove('active');
    this.isOpen = false;
  }

  initSizeGuide() {
    let dropdown = document.getElementById('size-selector');
    dropdown.addEventListener('change', function () {
      changeSize();
    });
  }
}

customElements.define('size-guide-drawer', SizeGuideDrawer);

class WishlistDrawer extends HTMLElement {
  connectedButtons = [];
  constructor() {
    super();

    this.hasAccount = ShopifyAnalytics.meta.page.customerId;

    if (this.hasAccount) {
      this.classList.remove('no-account');
    }

    let removeTranslation = window.translations['wishlist.remove'] || 'Remove';
    let addtoTranslation = window.translations['wishlist.addtocart'] || 'Add to bag';

    this.productCardMarkup = `
    <div class="swym-wishlist-grid">
      {{#products}} 
      <div href="{{du}}"aria-label="{{dt}}" class="swym-wishlist-item swym-is-anchor">
        <div class="swym-wishlist--container">
          <div class="swym-wishlist-image-wrapper">
            <img alt="" class="swym-wishlist-image" src="{{mainImg}}">
          </div>

          <div class="swym-wishlist--wrapper">
          
            <div class="swym-title swym-title-1 h5">
              {{dt}}

              <div class="swym-product-price swym-text swym-text-1">
                <div class="swym-product-final-price swym-value">
                  <span>$</span>{{pr}}
                </div>
              </div>
            </div>
           
            <div class="swym-variant-title swym-text swym-title-2 swym-variant-title-spacer h6 secondary-text-color fw-medium">
              {{variantinfo}}
            </div>

            <div class="swym-wishlist--ctas">
              <button 
                id="swym-custom-add-toCartBtn" 
                data-state-cart="{{#isInCart}} 
                swym-added{{/isInCart}}" 
                data-product-url="{{du}}" 
                data-variant-id="{{epi}}" 
                data-product-id="{{empi}}" 
                class="button button--secondary swym-is-button swym-is-button"
              >
                ${addtoTranslation}
              </button>

              <div id="swym-remove-productBtn" aria-label="Delete" data-variant-id="{{epi}}" data-product-id="{{empi}}" class="swym-delete-btn swym-nav swym-nav-1 swym-is-button">
                ${removeTranslation}
              </div>
            </div>

          </div>
        </div>
        
      </div>
      {{/products}}
    </div>`;

    this.connectButtons();

    document.addEventListener('custom:reloadWishlistDrawer', () => {
      this.connectButtons();
    });
  }

  connectButtons() {
    var openDrawer = document.querySelectorAll('.js-modal-drawer-open-wishlist');

    openDrawer.forEach((drawerBtn) => {
      if (!this.connectedButtons.includes(drawerBtn)) {
        drawerBtn.addEventListener('click', () => {
          this.classList.add('active');
          this.isOpen = true;
          this.initWishlist();
        });
        this.connectedButtons.push(drawerBtn);
      }
    });
  }

  connectedCallback() {
    var closeDrawer = this.querySelector('.icon-close');

    closeDrawer.addEventListener('click', () => {
      this.closeModal();
    });

    // clicking outside this should closeModal as well
    this.addEventListener('click', (event) => {
      if (event.target === this) {
        this.closeModal();
      }
    });

    // document.querySelector('.swym-add-to-wishlist').addEventListener('click', () => {
    //   console.log('Clicked');
    // });
  }

  closeModal() {
    this.classList.remove('active');
    this.isOpen = false;
  }

  initWishlist() {
    if (!window.SwymCallbacks) {
      window.SwymCallbacks = [];
    }

    window.SwymCallbacks.push(this.swymRenderWishlist.bind(this));
  }

  getVariantInfo(variants) {
    try {
      let variantKeys = variants && variants != '[]' ? Object.keys(JSON.parse(variants)[0]) : [],
        variantinfo;
      if (variantKeys.length > 0) {
        variantinfo = variantKeys[0];
        if (variantinfo == 'Default Title') {
          variantinfo = '';
        }
      } else {
        variantinfo = '';
      }
      return variantinfo;
    } catch (err) {
      return variants;
    }
  }

  swymRenderWishlist(swat) {
    // Get wishlist items
    swat.fetch((products) => {
      if (ShopifyAnalytics.meta.page.customerId == undefined) {
        return;
      }
      document.querySelector('.wishlist-count-bubble span').innerText = products.length !== 0 ? products.length : '';
      var wishlistContentsContainer = document.getElementById('wishlist-items-container');
      var formattedWishlistedProducts = products.map((p) => {
        p = SwymUtils.formatProductPrice(p); // formats product price and adds currency to product Object
        p.isInCart = _swat.platform.isInDeviceCart(p.epi) || p.et == _swat.EventTypes.addToCart;
        p.variantinfo = p.vi ? this.getVariantInfo(p.vi) : '';
        p.mainImg = p.iu.replace('&width=620', '');

        if (Shopify.locale == 'fr') {
          p.dt = p.cprops.product_title;
          p.variantinfo = p.cprops.variant_title ? p.cprops.variant_title : p.variantinfo;
        }
        return p;
      });
      var productCardsMarkup = SwymUtils.renderTemplateString(this.productCardMarkup, {
        products: formattedWishlistedProducts,
      });
      if (wishlistContentsContainer) {
        wishlistContentsContainer.innerHTML = productCardsMarkup;

        let emptyTranslation = window.translations['wishlist.empty'] || 'Your wishlist is empty';
        let continueTranslation = window.translations['general.continue_shopping'] || 'Continue Shopping';


        if (formattedWishlistedProducts.length == 0) {
          wishlistContentsContainer.innerHTML = `
          <div class="">
            <h5 class="fw-medium">${emptyTranslation}<h5/>
            <a href="#" class="button button--secondary w-100 fw-medium close-modal-btn">${continueTranslation}</a>
          </div>
          `;
        }

        this.attachClickListeners();
      } else {
        console.log('Container not found, Wishlist Page element not found');
      }
    });
  }

  attachClickListeners() {
    var addToCartButtons = document.querySelectorAll('#swym-custom-add-toCartBtn');
    var removeBtns = document.querySelectorAll('#swym-remove-productBtn');
    
    //   Add to cart Btns
    for (var i = 0; i < addToCartButtons.length; i++) {
      addToCartButtons[i].addEventListener('click', this.onAddToCartClick.bind(this), false);
    }
    //   Remove Buttons
    for (var k = 0; k < removeBtns.length; k++) {
      removeBtns[k].addEventListener('click', this.onRemoveBtnClick.bind(this), false);
    }

    var btnClose = this.querySelector('.close-modal-btn');

    btnClose.addEventListener('click', () => {
      this.closeModal();
    });
  }

  onAddToCartClick(e) {
    e.preventDefault();
    var productId = e.currentTarget.getAttribute('data-product-id');
    var variantId = e.currentTarget.getAttribute('data-variant-id');
    var du = e.target.getAttribute('data-product-url');
    let adding = window.translations['wishlist.adding'] || 'Adding...';
    let added = window.translations['wishlist.addedtocart'] || 'Added to Cart';

    e.target.innerHTML = adding;
    window._swat.replayAddToCart(
      {
        empi: productId,
        du: du,
      },
      variantId,
      (c) => {
        e.target.innerHTML = added;
        e.target.setAttribute('data-state-cart', 'swym-added');
        document.getElementById('CartDrawer-Checkout').removeAttribute('disabled');

        console.log('Successfully added product to cart.', c);
        this.fetchCartData();
      },
      function (e) {
        console.log(e);
      }
    );
  }

  onRemoveBtnClick(e) {
    e.preventDefault();
    var epi = +e.currentTarget.getAttribute('data-variant-id');
    var empi = +e.currentTarget.getAttribute('data-product-id');
    window._swat.fetch((products) => {
      products.forEach((product) => {
        if (epi && empi && product.epi == epi && product.empi == empi) {
          window._swat.removeFromWishList(product, () => {
            if (!window.SwymCallbacks) {
              window.SwymCallbacks = [];
            }
            window.SwymCallbacks.push(this.swymRenderWishlist.bind(this));
          });
        }
      });
    });
  }

  fetchCartData() {
    fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
        for (const selector of selectors) {
          const targetElement = document.querySelector(selector);
          const sourceElement = html.querySelector(selector);

          if (targetElement && sourceElement) {
            targetElement.replaceWith(sourceElement);
            document.getElementsByTagName('cart-drawer')[0].classList.remove('is-empty');
            document.getElementsByTagName('cart-drawer')[0].querySelector('.drawer__inner-empty')?.remove();
          }
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('wishlist-drawer', WishlistDrawer);

//Executed after SWYM has loaded all necessary resources.
function onSwymLoadCallback(swat) {
  // Please make all SWYM-related API calls inside this function.
  // You can use the swat argument within this scope.


  if (ShopifyAnalytics.meta.page.customerId == undefined) {
    document.querySelector('.js-wishlist-count-bubble span:not(.visually-hidden)').innerText = '';
    return;
  }

  window._swat.fetch((products) => {
    document.querySelector('.js-wishlist-count-bubble span:not(.visually-hidden)').innerText = products.length !== 0 ? products.length : '';
  });

  // Define the event
  let eventAdd = 'sw:addedtowishlist';
  let eventRemove = 'sw:removedfromwishlist';

  // Define the event listener function
  let listener = function (e) {
    const wishlistCounter = document.querySelector('.js-wishlist-count-bubble span:not(.visually-hidden)').innerText;
    
    let count = !wishlistCounter ? 0 : parseInt(wishlistCounter);

    if (e.type == 'sw:removedfromwishlist') {
      count = count - 1;
      if (count == 0) {
        count = '';
      }
    }

    if (e.type == 'sw:addedtowishlist') {
      count = count + 1;
    }

    document.querySelector('.js-wishlist-count-bubble span:not(.visually-hidden)').innerText = count;
  };

  // Add listener for the 'addedtowishlist' event to the Swym Event Layer
  swat.evtLayer.addEventListener(eventAdd, listener);
  swat.evtLayer.addEventListener(eventRemove, listener);
}

if (!window.SwymCallbacks) {
  window.SwymCallbacks = [];
}
window.SwymCallbacks.push(onSwymLoadCallback);

class ReadMore extends HTMLElement {
  constructor() {
    super();

    this.readMore = window.translations['sections.about.read_more'] || 'Read more';
    this.readLess = window.translations['sections.about.read_less'] || 'Read less';

    this.collapseState = false;
    this.readMoreElement = this.querySelector('.js-read-more');

    // hide read more if there's only 1 paragraph
    if (this.querySelectorAll('p').length < 0) {
      this.readMoreElement.style.display = 'none';
    }

    this.querySelectorAll('p').forEach((paragraph, index) => {
      if (index > 0) {
        paragraph.style.display = 'none';
        this.collapseState = true;
      }
    });

    this.readMoreElement?.addEventListener('click', (event) => {
      this.querySelectorAll('p').forEach((paragraph, index) => {
        if (index > 0) {
          if (this.collapseState) {
            paragraph.style.display = 'block';
          } else {
            paragraph.style.display = 'none';
          }
        }
      });

      if (this.collapseState) {
        this.readMoreElement.innerText = this.readLess;
      } else {
        this.readMoreElement.innerText = this.readMore;
      }

      this.collapseState = !this.collapseState;
    });
  }
}

if (!customElements.get('read-more')) {
  customElements.define('read-more', ReadMore);
}

class ContentAccordion extends HTMLElement {
  constructor() {
    super();

    this.querySelector('.js-title').addEventListener('click', (item) => {
      item.target.classList.toggle('active');
      this.querySelector('.js-content').classList.toggle('d-none');
    });
  }
}

customElements.define('content-accordion', ContentAccordion);

class ReadMoreComponent extends HTMLElement {
  constructor() {
    super();
    
    this.collapseState = true;
    this.originalParagraph = null;
    this.readMoreElement = this.querySelector(".js-read-more");
    this.readMore = window.translations['sections.about.read_more'] || 'Read more';
    this.readLess = window.translations['sections.about.read_less'] || 'Read less';

    this.readMoreElement.addEventListener("click",() => {
      this.collapseState = !this.collapseState;
      this.toggleText();

      if(this.collapseState) {
        this.readMoreElement.innerText = this.readMore;
      } else {
        this.readMoreElement.innerText = this.readLess;
      }
    });
    
   this.toggleText();
  }


  toggleText() {
    this.querySelectorAll('p').forEach((paragraph, index) => {
      if(this.collapseState == false) {
        if(index == 0) {
          paragraph.innerText = this.originalParagraph;
        }
        if (index > 0) {
          paragraph.style.display = 'block';
        }
      } else {
        if(index == 0) {
          this.originalParagraph = paragraph.innerText;
          paragraph.innerText = paragraph.innerText.slice(0,301) + "...";
        }
        if (index > 0) {
          paragraph.style.display = 'none';
        }
      }
    });
  }

}

if(!customElements.get('read-more-component')) {
  customElements.define("read-more-component", ReadMoreComponent);
}
